// @flow
import { Helmet } from 'react-helmet';
import get from 'lodash/get';
import mergeWith from 'lodash/mergeWith';
import getLinkTags from 'utils/getLinkTags';
import { ROOT_LOCALE } from 'utils/constants';
import type { PageContextType } from './PageContext';
import { imageTransform } from './Image';
import * as componentKeys from '../componentKeys';

type PropsType = {
  pageContext: PageContextType
};

const ogLocales = {
  en: 'en_US',
  ja: 'ja_JP',
  ko: 'ko_KR',
  fr: 'fr_FR'
};

export default function SeoContent({ pageContext }: PropsType) {
  const pageType = get(pageContext, 'story.field_component');
  const heroContent = get(pageContext, 'story.content.hero_content');
  const previewImage = get(pageContext, 'story.content.preview_image');
  const contentHeroImage = get(pageContext, 'story.content.hero_image');
  const shouldHidePageFromSearchEngines = get(
    pageContext,
    'story.content.hide_from_search_engines'
  );
  let lang = ROOT_LOCALE;
  if (pageContext.country && pageContext.country.code) {
    lang = pageContext.country.code;
  }
  const htmlAttributes = {
    lang
  };
  const linkTags = getLinkTags(pageContext.links);

  const mergedSeoDefinitions = mergeWith(
    {},
    get(pageContext, 'layout.default_seo', {}),
    get(pageContext, 'story.content.seo', {}),
    (a, b) => b || a
  );

  const { description, title } = mergedSeoDefinitions;
  let {
    og_description: ogDescription,
    og_title: ogTitle,
    twitter_description: twitterDescription,
    twitter_title: twitterTitle,
    og_image: ogImage,
    twitter_image: twitterImage
  } = mergedSeoDefinitions;

  if (!ogTitle) {
    ogTitle = title;
  }
  if (!ogDescription) {
    ogDescription = description;
  }
  if (!twitterTitle) {
    twitterTitle = title;
  }
  if (!twitterDescription) {
    twitterDescription = description;
  }
  let ogType = 'website';
  let twitterType = 'product';
  if (
    pageType === componentKeys.BLOG_AGGREGATE_PAGE ||
    pageType === componentKeys.BLOG_POST
  ) {
    ogType = 'blog';
    twitterType = 'summary';
  }
  if (!ogImage || !twitterImage) {
    let heroImage;
    if (heroContent && heroContent.length) {
      // Normal Page
      heroImage = heroContent[0].image;
    } else if (previewImage) {
      // Use Blog Page Preview by preference
      heroImage = previewImage;
    } else if (contentHeroImage) {
      // If for some reason Blog Preview isn't set, use the blog hero image
      heroImage = contentHeroImage;
    }
    ogImage = ogImage || heroImage;
    twitterImage = twitterImage || heroImage;
  }
  if (!title) {
    throw new Error(
      `Page ${get(
        pageContext,
        'story.full_slug',
        'unknown page'
      )} is missing a SEO Title and the country/language default has not been set`
    );
  }
  return (
    <Helmet htmlAttributes={htmlAttributes}>
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      {ogTitle && <meta name="og:title" content={ogTitle} />}
      {ogDescription && <meta name="og:description" content={ogDescription} />}
      {ogImage && (
        <meta
          name="og:image"
          content={imageTransform(ogImage, {
            size: { width: 1080, height: 1080 }
          })}
        />
      )}
      {twitterTitle && <meta name="twitter:title" content={twitterTitle} />}
      {twitterDescription && (
        <meta name="twitter:description" content={twitterDescription} />
      )}
      {twitterImage && (
        <meta
          name="twitter:image"
          content={imageTransform(twitterImage, {
            size: { width: 1080, height: 1080 }
          })}
        />
      )}
      <meta
        name="og:locale"
        content={
          ogLocales[(pageContext.rootSlug || `/${ROOT_LOCALE}`).substring(1)]
        }
      />
      <meta name="og:type" content={ogType} />
      <meta name="twitter:card" content={twitterType} />
      {shouldHidePageFromSearchEngines && (
        <meta name="robots" content="noindex" />
      )}
      {linkTags}
    </Helmet>
  );
}
